import type { SpecificTaskInfo, TaskInfo } from '@core/transmission/ITask'
import { TaskError, TaskState, TransmissionTaskType } from '@core/transmission/ITask'
import { AbsTask, SUCCESS_IN_PERCENT } from '@core/transmission/AbsTask'
import { INSTANCE as DeviceManager } from '../devices/DeviceManager'
import type { FileInfo, Listener } from './IReadOnlyFileTransmissionManager'
import type { FileTransmissionError, IFileTransmissionProgressListener } from '../FileTransmissionProgressListener'
import CloudStorage from '@/cloudstorage/CloudStorage'
import { HttpRequestCancelToken } from '../HttpRequestCancelToken'
import { isInIosAPP } from '../Utils'
import { INSTANCE as NativeDownloadManager } from '@nativeAPP/nativeDownload/NativeDownloadManager'
import { DeviceTransmissionService } from './DeviceTransmissionService'
import { delay } from 'lodash-es'

export class AbsDownloadTask extends AbsTask {
  protected _parentPath: string
  protected _downloadUrl = ''
  protected _ObjectUrl = ''
  protected _progressListener: IFileTransmissionProgressListener
  protected _completionRatio = 0
  protected _isInDeviceSideExecutingStage = false
  protected _fileDownloadListener: Listener | undefined
  protected _fileType: string
  protected _fileFormat: string
  protected _sourceTaskType: TransmissionTaskType | undefined
  protected _transmissionTaskType: TransmissionTaskType = TransmissionTaskType.DOWNLOAD
  constructor(deviceId: string, fileInfo: FileInfo, isViaLan: boolean, appId: string, mirrorTask?: SpecificTaskInfo) {
    super(deviceId, isViaLan, appId)

    this._parentPath = fileInfo.parentPath
    this._fileName = fileInfo.fileName
    this._fileSize = fileInfo.fileSize
    this._fileType = fileInfo.fileType
    this._fileFormat = fileInfo.fileFormat
    this._downloadUrl = ''

    if (mirrorTask) {
      this.id = mirrorTask.id
      this.sequenceId = mirrorTask.sequenceId
      this._sourceTaskType = mirrorTask.sourceTaskType
      this._state = TaskState.WAITING
    }
    this._progressListener = {
      onStart: (id) => {
        //  do nothing
      },
      onProgressChanged: (transmitted, total, progressMap?: Map<number, number>) => {
        if (this.isDeleted) return
        this.onProgressChanged(transmitted, total, progressMap)
      },
      onCompleted: (fileUrl?: string) => {
        if (this.isDeleted) return
        this.onCompleted(fileUrl)
      },
      onCancelled: (isRemote: boolean) => {
        if (this.isDeleted) return
        this.onCancelled(isRemote)
      },
      onError: (error) => {
        if (this.isDeleted) return
        this.onError(error)
      },
    }
  }

  get downloadUrl(): string {
    return this._downloadUrl
  }

  setDownloadListener(listener: Listener) {
    this._fileDownloadListener = listener
  }

  onProgressChanged(transmitted: number, total: number, progressMap?: Map<number, number>): void {
    // console.log(
    //   `[Transmission]: onProgressChanged ${this._fileName} ${new Date().toLocaleString()}`,
    //   transmitted,
    //   total
    // )
    let transmittedProgress = 0

    if (progressMap) {
      for (const progress of progressMap.values()) {
        transmittedProgress += progress
      }
    } else {
      transmittedProgress = transmitted
    }

    this._isInDeviceSideExecutingStage = false
    // console.log(
    //   `[Transmission] ${this._isViaLan ? 'lan' : 'wan'} download task ${new Date().toLocaleString()} ${
    //     this._fileName
    //   } 下载任务进度更新:`,
    //   transmittedProgress,
    //   total
    // )
    if (this._state === TaskState.FAILED || this._state === TaskState.SUCCESS) return
    const completionRatio = Math.floor((transmittedProgress / total) * 50) + 50

    // console.log(
    //   `[Transmission] ${this._isViaLan ? 'lan' : 'wan'} download task ${new Date().toLocaleString()} ${
    //     this._fileName
    //   } 下载任务进度更新:`,
    //   completionRatio
    // )
    this._completionRatio = completionRatio > this._completionRatio ? completionRatio : this._completionRatio
    this._taskScheduler?.updateTask(this)
    this._fileDownloadListener?.onProgressChanged?.(transmittedProgress, total, this._deviceId, false)
  }

  onCompleted(fileUrl?: string, isStreamingDownload?: boolean): void {
    this._completionRatio = SUCCESS_IN_PERCENT
    this._ObjectUrl = fileUrl ?? ''
    this._taskScheduler?.setTaskSucceed(this)

    this._deleteZipFile(isStreamingDownload)
  }

  onCancelled(isRemote: boolean | undefined): void {
    if (isRemote) {
      this._taskScheduler?.setTaskError(this)
    }
  }

  onError(error: FileTransmissionError): void {
    this._failedReason = TaskError.TRANSFER_FAILED
    this._handleError(error)
  }

  toJson(): TaskInfo {
    let deviceName = ''
    const device = DeviceManager.getDeviceById(this._deviceId)

    if (device) deviceName = device.model

    return {
      ...super.toJson(),
      downloadUrl: this._downloadUrl,
      completionRatio: this._getCompletionRation(),
      deviceName,
      path: this._parentPath,
      objectUrl: this._ObjectUrl,
    }
  }

  protected _handleError(error: unknown): void {
    console.log(
      `[Transmission]: _handleError ${this._fileName} ${new Date().toLocaleString()}`,
      error,
      this.failedReason
    )

    this._taskScheduler?.setTaskError(this)
    this._fileDownloadListener?.onError(this._deviceId, this._parentPath, this._fileName, this.failedReason as string)
  }

  protected _onDeviceSideExecuteCompleted(fileUrl: string, isLan?: boolean, isSupportRange?: boolean) {
    console.log(
      `[Transmission]: _onDeviceSideExecuteCompleted ${this._fileName} ${new Date().toLocaleString()}`,
      fileUrl
    )

    this._isInDeviceSideExecutingStage = false

    this._requestCancelToken = new HttpRequestCancelToken()

    if (isInIosAPP()) {
      NativeDownloadManager.download({
        id: this.id,
        url: fileUrl,
        name: this._fileName,
        size: this._fileSize,
        progressListener: this._progressListener,
        requestCancelToken: this._requestCancelToken,
        task: this,
        fileType: this._fileType,
        fileFormat: this._fileFormat,
      })

      return
    }
    CloudStorage.INSTANCE.downloadFile(
      { url: fileUrl, size: this._fileSize, name: this._fileName },
      this._progressListener,
      this._requestCancelToken,
      isLan,
      isSupportRange
    )
  }

  protected _getCompletionRation(): number {
    // console.log(
    //   `[Transmission]: _getCompletionRation ${this._fileName} _isInDeviceSideExecutingStage ${
    //     this._isInDeviceSideExecutingStage
    //   } ${new Date().toLocaleString()}`,
    //   this._completionRatio
    // )

    return this._completionRatio
  }

  protected _deleteZipFile(isStreamingDownload = true) {
    console.log(
      `[Transmission]: _deleteZipFile ${this._fileName} ${new Date().toLocaleString()} this._sourceTaskType:${
        this._sourceTaskType
      }, isStreamingDownload:`,
      isStreamingDownload
    )

    if (this._sourceTaskType === TransmissionTaskType.ZIP && isStreamingDownload) {
      // 避免端侧对压缩文件可能存在的引用问题
      delay(() => {
        const device = this._getDevice()

        device && DeviceTransmissionService.deleteFile(device, this._parentPath + this._fileName, this._fileName)
      }, 2000)
    }
  }
}
